import { css, Global } from '@emotion/react';
import theme from './theme';

import typography from 'storybookConfig/mixins/typography';
import materialSymbol from 'storybookConfig/mixins/materialSymbol';

const globalStyles = css`
  html {
    box-sizing: border-box;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  body {
    margin: 0;
    height: 100vh;
  }

  // App container wraps the entire app

  .app-container {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .main-content {
    overflow-y: scroll;
  }

  // Additional portal containers
  .alerts-container {
    position: relative;
    z-index: 1051; // React modals are 1050
  }

  // Helpers

  .visuallyhidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
  }

  .fixFirstTdPadding.fixFirstTdPadding.fixFirstTdPadding {
    // Useful for overriding the theme's '.card-table tbody td:first-of-type'
    // padding when using a 'rowSpan' prop on a 'td'.
    padding-left: 16px !important;
  }
`;

const typographyStyles = css`
  body {
    ${typography('Body/Regular')}
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  code {
    font-family: ${theme.font.monospace};
    color: ${theme.color.bodyTextPrimary};
    background-color: ${theme.color.surfaceCream};
    display: inline-block;
    padding: 0 0.4em;
    border-radius: 0.25rem;
  }
`;

const bootstrapStylesOverride = css`
  // Override Theme Variables

  :root {
    // There is no direct replacement for indigo, purple, orange, or pink.
    // We're choosing not to override the default bootstrap breakpoints.
    --blue: ${theme.color.actionFill};
    --red: ${theme.color.errorBase};
    --yellow: ${theme.color.warningBase};
    --green: ${theme.color.successBase};
    --teal: ${theme.color.successBase};
    --cyan: ${theme.color.infoBase};
    --white: ${theme.color.surfaceCanvas};
    --gray: ${theme.color.bodyTextCaption};
    --gray-dark: ${theme.color.bodyTextPrimary};
    --primary: ${theme.color.actionFill};
    --secondary: ${theme.color.bodyTextCaption};
    --success: ${theme.color.successBase};
    --info: ${theme.color.infoBase};
    --warning: ${theme.color.warningBase};
    --danger: ${theme.color.errorBase};
    --light: ${theme.color.surfaceSubtle};
    --dark: ${theme.color.bodyTextPrimary};

    --font-family-sans-serif: ${theme.font.primary};
    --font-family-monospace: ${theme.font.monospace};
  }

  // App Wrapper

  .main-content {
    flex: 1;
    overflow-x: auto;
    background-color: ${theme.color.bodyBackground};
  }

  .navbar-vertical:not([style*='display: none']) ~ .main-content .container-fluid.container-fluid {
    // The same on both sides, so the content sits centred between the sidebar and the window.
    padding-left: 40px !important;
    padding-right: 40px !important;
  }

  .container-marketplace {
    padding-bottom: 80px;
    position: relative;
  }

  // Bootstrap theme colours that are written as hex in theme.min.css rather than read from the
  // variables above: the primary button's hover, focus and active states, the white button,
  // form controls, and hairlines.

  .btn-primary:hover,
  .btn-primary:focus,
  .btn-primary.focus,
  .btn-primary:not(:disabled):not(.disabled):active,
  .show > .btn-primary.dropdown-toggle,
  a.badge-primary:hover,
  a.badge-primary:focus,
  a.bg-primary:hover,
  button.bg-primary:hover {
    background-color: ${theme.color.actionFillHover};
    border-color: ${theme.color.actionFillHover};
  }

  /*
   * Dashkit rings every focused button in its own blue, a mouse click included, through 39 rules
   * up to .btn-primary:not(:disabled):not(.disabled):active:focus. Clear those, and ring keyboard
   * focus only; !important is the one way past that specificity.
   */
  .btn:focus,
  .btn.focus {
    box-shadow: none !important;
  }

  .btn:focus-visible {
    box-shadow:
      0 0 0 2px ${theme.color.surfaceCanvas},
      0 0 0 4px ${theme.color.focusRing} !important;
  }

  .btn-white {
    color: ${theme.color.bodyTextPrimary};
    border-color: ${theme.color.border};

    &:hover,
    &:focus {
      color: ${theme.color.bodyTextPrimary};
      background-color: ${theme.color.surfaceSubtle};
      border-color: ${theme.color.borderMedium};
    }
  }

  .form-control,
  .custom-select,
  .custom-file-label,
  .input-group-text {
    border-color: ${theme.color.inputBorder};
    background-color: ${theme.color.inputBackground};

    &:focus {
      border-color: ${theme.color.inputBorderFocus};
    }

    &:disabled,
    &[readonly] {
      background-color: ${theme.color.inputBackgroundDisabled};
    }
  }

  /* Bootstrap's fields drawn as the Input molecule until their screens move to it: 44px, the field
     radius, the input type, and an iris border on focus instead of Dashkit's glow. \`-sm\` is the
     same field; the app has one size. Inner edges of an input group stay square (Bootstrap's own,
     more specific rules). */
  .form-control,
  .form-control-sm {
    height: auto;
    min-height: 44px;
    padding: 10px 16px;
    border-radius: ${theme.radius.field};
    ${typography('Inputs/Input')};

    &:focus {
      box-shadow: none;
    }

    &::placeholder {
      color: ${theme.color.bodyTextSecondary};
    }
  }

  textarea.form-control {
    border-radius: ${theme.radius.xl};
  }

  .input-group-text {
    padding: 0 16px;
    border-radius: ${theme.radius.field};
    color: ${theme.color.bodyTextSecondary};
    ${typography('Inputs/Input')};
  }

  .input-group.input-group-merge {
    > .form-control {
      border-radius: ${theme.radius.field};
    }

    > .input-group-prepend > .input-group-text {
      padding-left: 16px;
      border-top-left-radius: ${theme.radius.field};
      border-bottom-left-radius: ${theme.radius.field};
    }

    > .input-group-append > .input-group-text {
      padding-right: 16px;
      border-top-right-radius: ${theme.radius.field};
      border-bottom-right-radius: ${theme.radius.field};
    }
  }

  /* A borderless search box at the top of a list keeps its size and square edges. */
  .input-group-flush > .form-control {
    min-height: 0;
    height: calc(1.5em + 1rem + 2px);
    border-radius: 0;
  }

  .form-control.is-invalid {
    border-color: ${theme.color.errorBase};

    &:focus {
      border-color: ${theme.color.errorBase};
      box-shadow: none;
    }
  }

  /* Without Bootstrap's error icon, which would sit where the DS field has none. */
  .form-control.is-invalid {
    padding-right: 16px;
    background-image: none;
  }

  .invalid-feedback {
    ${typography('Body/Body Small')};
    color: ${theme.color.errorBase};
  }

  .custom-control-label::before {
    border-color: ${theme.color.controlBorder};
  }

  // Bootstrap's switch drawn as the Toggle molecule: a 40 x 20 track, a 16px white handle, iris
  // when on. Legacy screens (partner actions, settings) still use the custom-switch markup.
  .custom-switch {
    padding-left: 48px;
    min-height: 20px;

    .custom-control-label {
      min-height: 20px;
      cursor: pointer;
    }

    .custom-control-label::before {
      left: -48px;
      top: 2px;
      width: 40px;
      height: 20px;
      border: 0;
      border-radius: 20px;
      background-color: ${theme.color.surfaceStrong};
      box-shadow: none;
    }

    .custom-control-label::after {
      left: -46px;
      top: 4px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: ${theme.color.surfaceCanvas};
      transition: transform ${theme.motion.hover} ${theme.motion.easeOut};
    }

    .custom-control-input:checked ~ .custom-control-label::before {
      background-color: ${theme.color.accent};
    }

    .custom-control-input:checked ~ .custom-control-label::after {
      background-color: ${theme.color.surfaceCanvas};
      transform: translateX(20px);
    }

    .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: none;
    }

    .custom-control-input:focus-visible ~ .custom-control-label::before {
      outline: 2px solid ${theme.color.focusRing};
      outline-offset: 2px;
    }

    .custom-control-input:disabled ~ .custom-control-label::before {
      background-color: ${theme.color.surfaceStrong};
    }
  }

  /* Bootstrap's border utilities set their colour with !important, so recolouring them needs it. */
  .border,
  .border-top,
  .border-right,
  .border-bottom,
  .border-left {
    border-color: ${theme.color.border} !important;
  }

  /* Components come after Dashkit in the cascade, so they need no !important and a status or
     selected border set on them still wins. */
  .list-group-item,
  .dropdown-menu,
  .table td,
  .table th,
  .card {
    border-color: ${theme.color.border};
  }

  .dropdown-item.active,
  .dropdown-item:active {
    color: ${theme.color.bodyTextPrimary};
    background-color: ${theme.color.accentSubtle};
  }

  // Dropdown Menus

  .DropdownMenu__no-minw.DropdownMenu__no-minw {
    min-width: inherit;

    .dropdown-item {
      padding: 0.374rem 0.8rem;
    }
  }

  // Replace Bootstrap's caret on Dropdown Toggles (the page-length picker in pagination)
  .dropdown-toggle {
    ${materialSymbol({ name: 'chevron-down', position: 'after', size: '16px' })}

    &::after {
      display: inline-block;
      vertical-align: middle;
      border: 0;
      margin-left: 4px;
    }
  }

  // Typography

  h1,
  .h1,
  .display-1 {
    ${typography('Headings/H1')}
  }

  h2,
  .h2,
  .display-2 {
    ${typography('Headings/H2')}
  }

  h3,
  .h3,
  .display-3 {
    ${typography('Headings/H3')}
  }

  h4,
  .h4,
  .display-4 {
    ${typography('Headings/H4')}
  }

  h5,
  .h5 {
    ${typography('Body/Header')}
  }

  h6,
  .h6 {
    ${typography('Body/Caption')}
  }

  .text-muted {
    color: ${theme.color.bodyTextCaption} !important;
  }

  a {
    color: ${theme.color.bodyTextLinks};
    text-decoration: none;

    &:hover {
      color: ${theme.color.bodyTextLinksHover};
      text-decoration: underline;
    }
  }

  // Sentence case, never uppercase: the Bootstrap theme uppercases table headers, sidebar
  // headings and pretitles.
  .table thead th {
    ${typography('Body/Header')}
    text-transform: none;
    color: ${theme.color.bodyTextCaption};
    background-color: ${theme.color.surfaceSubtle};
  }

  .navbar-heading,
  .header-pretitle {
    text-transform: none;
    letter-spacing: -0.01em;
  }

  // Forms

  .form-label {
    ${typography('Body/Header')}
    margin-bottom: 8px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .invalid-feedback {
    display: block;
  }
`;

const GlobalStyles = () => {
  return (
    <>
      <Global styles={globalStyles} />
      <Global styles={typographyStyles} />
      <Global styles={bootstrapStylesOverride} />
    </>
  );
};

export default GlobalStyles;
